import { useTranslation } from 'react-i18next';
import { useEducationForm } from '../hooks/useEducationForm';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomDatePicker from '@shared/components/CustomDatePicker';

export default function EducationForm({ person }) {
    const { t } = useTranslation();
    const {
        educationFormRegister,
        educationErrors,
        educationData,
        handleEdit,
        handleDelete,
        isEditing,
        handleSubmit,
        submitEducationForm,
        handleCancel,
        degreeOptions,
        institutionOptions,
        watch,
        setValue
    } = useEducationForm(person);

    const formatDisplayDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        }
        return dateString;
    };

    const onEducationSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(submitEducationForm)(e);
    };

    return (
        <div className="education-form-container">
            <h3 className="form-subtitle">
                {t('persons.education.title', {
                    name: [person.name, person.fathersName, person.surname]
                        .filter(Boolean)
                        .join(' ')
                })}
            </h3>

            <div className="form-content">
                <div className="form-section">
                    <form onSubmit={onEducationSubmit}>
                        <div className="form-input-wrapper flex-row">
                            <div className='form-input-fields width-50'>
                                <span className="span-bold">{t('persons.education.degree')} <span className="required">*</span></span>
                                <select
                                    name="degreeLevelId"
                                    className={`form-control ${educationErrors.degreeLevelId ? 'is-invalid' : ''}`}
                                    {...educationFormRegister('degreeLevelId')}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {degreeOptions?.map(degree => (
                                        <option key={degree.value} value={degree.value}>
                                            {degree.label}
                                        </option>
                                    ))}
                                </select>
                                {educationErrors.degreeLevelId && (
                                    <div className="invalid-feedback">{educationErrors.degreeLevelId.message}</div>
                                )}
                            </div>
                            <div className='form-input-fields width-50'>
                                <span className="span-bold">{t('persons.education.specialty')} </span>
                                <input
                                    type="text"
                                    name="specialty"
                                    placeholder={t('persons.education.specialty')}
                                    className={`form-control ${educationErrors.specialty ? 'is-invalid' : ''}`}
                                    {...educationFormRegister('specialty')}
                                />
                                {educationErrors.specialty && (
                                    <div className="invalid-feedback">{educationErrors.specialty.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.education.diplomaNumber')}</span>
                                <div className='form-input-fields'>
                                    <input
                                        type="text"
                                        name="docNo"
                                        placeholder={t('persons.education.diplomaNumber')}
                                        className={`form-control ${educationErrors.docNo ? 'is-invalid' : ''}`}
                                        {...educationFormRegister('docNo')}
                                    />
                                    {educationErrors.docNo && (
                                        <div className="invalid-feedback">{educationErrors.docNo.message}</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.education.diplomaDate')} </span>
                                <div className='form-input-fields'>
                                    <CustomDatePicker
                                        name="docDate"
                                        value={watch('docDate')}
                                        onChange={(date) => {
                                            setValue('docDate', date, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                                shouldTouch: true
                                            });
                                        }}
                                        errors={educationErrors}
                                        placeholderText={t('common.dateFormat')}
                                        maxDate={new Date()}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-input-wrapper flex-row">
                            <div className='form-input-fields width-50'>
                                <span className="span-bold">{t('persons.education.institution')} <span className="required">*</span></span>
                                <select
                                    name="hrEducationInstId"
                                    className={`form-control ${educationErrors.hrEducationInstId ? 'is-invalid' : ''}`}
                                    {...educationFormRegister('hrEducationInstId')}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {institutionOptions?.map(institution => (
                                        <option key={institution.value} value={institution.value}>
                                            {institution.label}
                                        </option>
                                    ))}
                                </select>
                                {educationErrors.hrEducationInstId && (
                                    <div className="invalid-feedback">{educationErrors.hrEducationInstId.message}</div>
                                )}
                            </div>

                            <div className='form-input-fields width-50'>
                                <span className="span-bold">{t('persons.education.notes')}</span>
                                <input
                                    type="text"
                                    name="note"
                                    placeholder={t('persons.education.notes')}
                                    className={`form-control ${educationErrors.note ? 'is-invalid' : ''}`}
                                    {...educationFormRegister('note')}
                                />
                                {educationErrors.note && (
                                    <div className="invalid-feedback">{educationErrors.note.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.education.graduationDate')} <span className="required">*</span></span>
                            <div className='form-input-fields'>
                                <CustomDatePicker
                                    name="graduationDate"
                                    value={watch('graduationDate')}
                                    onChange={(date) => {
                                        setValue('graduationDate', date, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true
                                        });
                                    }}
                                    errors={educationErrors}
                                    placeholderText={t('common.dateFormat')}
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>

                        <div className="education-form-controls">
                            <button
                                type="button"
                                className={`form-btn ${isEditing ? 'btn-orange' : 'btn-green'}`}
                                onClick={handleSubmit(submitEducationForm)}
                            >
                                {isEditing ? t('common.edit') : t('common.add')}
                            </button>
                            {isEditing && (
                                <button
                                    type="button"
                                    className="form-btn btn-gray"
                                    onClick={handleCancel}
                                >
                                    {t('common.cancel')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                <div className="table-section">
                    <div className="education-list-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('persons.education.degree')}</th>
                                    <th>{t('persons.education.specialty')}</th>
                                    <th>{t('persons.education.diploma')}</th>
                                    <th>{t('persons.education.institution')}</th>
                                    <th>{t('persons.education.graduationDate')}</th>
                                    <th>{t('persons.education.notes')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {educationData?.map((education, index) => (
                                    <tr key={index}>
                                        <td>{education.degreeLevelDegreeLevel}</td>
                                        <td>{education.specialty}</td>
                                        <td>
                                            {education.docNo
                                                ? education.docDate
                                                    ? `${education.docNo} / ${formatDisplayDate(education.docDate)}`
                                                    : education.docNo
                                                : ''
                                            }
                                        </td>
                                        <td>{education.hrEducationInstName}</td>
                                        <td>{formatDisplayDate(education.graduationDate)}</td>
                                        <td>{education.note}</td>
                                        <td className="action-buttons">
                                            <FontAwesomeIcon
                                                icon={faEdit}
                                                className="action-icon edit"
                                                onClick={() => handleEdit(education)}
                                            />
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                className="action-icon delete"
                                                onClick={() => handleDelete(education.id)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
} 